import { Check, ImagePlus, Loader2, PenLine, Sparkles, Star, X } from "lucide-react";
import { cloneElement, type ReactElement, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import type { CampaignSlotCardData } from "@/components/campaign/campaign-slot-card";
import { SocialPostPreviewCard } from "@/components/campaign/social-post-preview-card";
import {
  resolveChatGeneratedAssetAspectRatio,
  resolveChatGeneratedMediaDimensionsAspectRatio,
} from "@/lib/chat-generated-media-aspect-ratio";
import { resolveAssistantSelectedAssetPreview } from "@/lib/assistant-selected-asset-preview";
import { cn } from "@/lib/utils";
import { Badge } from "@runcheetah/ui";

import type {
  ChatGeneratedAsset,
  ChatGeneratedAssetLogoAsset,
  ChatGeneratedImage,
} from "./assistant-chat-media-gallery-types";
import {
  getGeneratedAssetCardStatusLabel,
  getGeneratedAssetPost,
} from "./assistant-chat-media-gallery-view-model";

export function ChatGeneratedPostAssetVariantCard({
  asset,
  profileLabel,
  logoAssets,
  isPrimary,
  variantIndex,
  variantCount,
  approvePending,
  rejectPending,
  makePrimaryPending,
  onEdit,
  onOpenCampaign,
  onUseInChat,
  onChangeCopyInChat,
  onMakePrimary,
  onApprove,
  onReject,
}: {
  asset: ChatGeneratedAsset;
  profileLabel: string;
  logoAssets: ChatGeneratedAssetLogoAsset[];
  isPrimary: boolean;
  variantIndex: number;
  variantCount: number;
  approvePending: boolean;
  rejectPending: boolean;
  makePrimaryPending: boolean;
  onEdit: () => void;
  onOpenCampaign: () => void;
  onUseInChat: () => void;
  onChangeCopyInChat?: () => void;
  onMakePrimary: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const intl = useIntl();
  const statusLabel = getGeneratedAssetCardStatusLabel(asset, intl);
  const editLabel = intl.formatMessage({ id: "common.edit", defaultMessage: "Edit" });
  const post = getGeneratedAssetPost(asset);
  const preview = resolveAssistantSelectedAssetPreview({ asset, logoAssets });
  const isReviewable = Boolean(
    asset.scheduledPostId && post?.status === "generated" && post.approvalStatus === "pending",
  );
  const isApprovedCampaignAsset =
    asset.batchSourceType === "campaign" && post?.approvalStatus === "approved";
  const canEdit = !post || post.approvalStatus === "pending";
  const canMakePrimary = Boolean(
    post && post.approvalStatus === "pending" && !isPrimary && canSetAssetAsPrimary(asset),
  );
  const canChangeCopy = Boolean(
    asset.scheduledPostId && post && post.approvalStatus !== "approved" && onChangeCopyInChat,
  );

  if (!preview || !post) {
    return (
      <ChatGeneratedRawAssetCard
        asset={asset}
        variantIndex={variantIndex}
        variantCount={variantCount}
        onUseInChat={onUseInChat}
      />
    );
  }

  return (
    <article className="min-w-0">
      <SocialPostPreviewCard
        preview={preview}
        timeLabel={statusLabel}
        profileLabel={profileLabel}
        muted={post.approvalStatus === "rejected"}
        mediaOverlay={
          <>
            {canEdit ? <EditImageOverlayButton label={editLabel} onEdit={onEdit} /> : null}
            {isApprovedCampaignAsset ? (
              <OpenCampaignOverlayButton onOpenCampaign={onOpenCampaign} />
            ) : null}
            <VariantBadges variantIndex={variantIndex} variantCount={variantCount} />
            <ChatGeneratedAssetActions
              reviewable={isReviewable}
              canMakePrimary={canMakePrimary}
              approvePending={approvePending}
              rejectPending={rejectPending}
              makePrimaryPending={makePrimaryPending}
              canChangeCopy={canChangeCopy}
              approvalStatus={post.approvalStatus}
              approveLabel={
                asset.batchSourceType === "campaign"
                  ? intl.formatMessage({
                      id: "pulse.mediaGallery.approveCampaignPreview",
                      defaultMessage: "Approve preview",
                    })
                  : intl.formatMessage({ id: "common.approve", defaultMessage: "Approve" })
              }
              onChangeCopy={onChangeCopyInChat ?? (() => {})}
              onMakePrimary={onMakePrimary}
              onApprove={onApprove}
              onReject={onReject}
            />
            <UseInChatOverlayButton onUseInChat={onUseInChat} />
          </>
        }
        className="rounded-2xl shadow-none transition hover:shadow-[0_16px_34px_-26px_rgba(15,15,16,0.5)]"
        captionFallback={asset.prompt ?? asset.batchName}
      />
    </article>
  );
}

function EditImageOverlayButton({ label, onEdit }: { label: string; onEdit: () => void }) {
  return (
    <button
      type="button"
      className="absolute inset-0 z-10 cursor-pointer bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/45"
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        onEdit();
      }}
    />
  );
}

function OpenCampaignOverlayButton({ onOpenCampaign }: { onOpenCampaign: () => void }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/0 opacity-0 transition-[background-color,opacity] duration-200 group-hover:bg-black/12 group-hover:opacity-100 group-focus-within:bg-black/12 group-focus-within:opacity-100">
      <button
        type="button"
        className="pointer-events-auto rounded-full bg-background/94 px-4 py-2 text-xs font-bold text-foreground shadow-lg shadow-black/15 backdrop-blur transition hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
        onClick={onOpenCampaign}
      >
        <FormattedMessage id="pulse.mediaGallery.openCampaign" defaultMessage="Open campaign" />
      </button>
    </div>
  );
}

function VariantBadges({
  variantIndex,
  variantCount,
}: {
  variantIndex: number;
  variantCount: number;
}) {
  return (
    <div className="pointer-events-none absolute left-2 top-2 z-20 flex max-w-[calc(100%-4rem)] flex-wrap gap-1.5">
      {variantCount > 1 ? (
        <Badge className="rounded-full bg-background/88 text-[10px] text-foreground shadow-sm backdrop-blur">
          <FormattedMessage
            id="pulse.mediaGallery.variantPosition"
            defaultMessage="Variant {current}/{total}"
            values={{ current: variantIndex, total: variantCount }}
          />
        </Badge>
      ) : null}
    </div>
  );
}

function UseInChatOverlayButton({ onUseInChat }: { onUseInChat: () => void }) {
  return (
    <div className="pointer-events-none absolute inset-x-2 bottom-2 z-20 flex justify-center opacity-100 transition sm:translate-y-1 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100 sm:group-focus-within:translate-y-0 sm:group-focus-within:opacity-100">
      <button
        type="button"
        className="pointer-events-auto inline-flex h-8 max-w-full items-center justify-center gap-1.5 rounded-full bg-background/92 px-3 text-[11px] font-bold text-foreground shadow-lg shadow-black/15 backdrop-blur transition hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
        onClick={(event) => {
          event.stopPropagation();
          onUseInChat();
        }}
      >
        <Sparkles className="h-3 w-3 text-primary" aria-hidden />
        <FormattedMessage id="pulse.mediaGallery.useInChat" defaultMessage="Use in chat" />
      </button>
    </div>
  );
}

function ChatGeneratedRawAssetCard({
  asset,
  variantIndex,
  variantCount,
  onUseInChat,
}: {
  asset: ChatGeneratedAsset;
  variantIndex: number;
  variantCount: number;
  onUseInChat: () => void;
}) {
  const aspectRatio = resolveChatGeneratedAssetAspectRatio(asset);

  return (
    <article className="group min-w-0 overflow-hidden rounded-2xl bg-card shadow-sm">
      <div className="relative overflow-hidden bg-muted" style={{ aspectRatio }}>
        <img
          src={asset.signedUrl}
          alt={asset.prompt ?? asset.batchName}
          className="h-full w-full object-contain"
          loading="lazy"
        />
        <VariantBadges variantIndex={variantIndex} variantCount={variantCount} />
        <UseInChatOverlayButton onUseInChat={onUseInChat} />
      </div>
    </article>
  );
}

export function ChatGeneratedImageCard({
  image,
  savePending,
  onUseInChat,
  onUseAsVisualUniverseSeed,
  onSave,
  onSaveAsReference,
}: {
  image: ChatGeneratedImage;
  savePending: boolean;
  onUseInChat: () => void;
  onUseAsVisualUniverseSeed?: () => void;
  onSave: () => void;
  onSaveAsReference: () => void;
}) {
  const aspectRatio = resolveChatGeneratedMediaDimensionsAspectRatio(image);

  return (
    <article className="group relative rounded-2xl bg-card text-left shadow-sm transition hover:shadow-[0_16px_34px_-26px_rgba(15,15,16,0.5)]">
      <div
        className="relative block w-full overflow-hidden rounded-2xl bg-muted text-left"
        style={{ aspectRatio }}
      >
        <img
          src={image.signedUrl}
          alt={image.prompt ?? "Generated image"}
          className="h-full w-full object-contain transition duration-300 group-hover:scale-[1.035]"
          loading="lazy"
        />
      </div>
      <ChatGeneratedImageActions
        savePending={savePending}
        onSave={onSave}
        onSaveAsReference={onSaveAsReference}
        onUseAsVisualUniverseSeed={onUseAsVisualUniverseSeed}
      />
      <UseInChatOverlayButton onUseInChat={onUseInChat} />
    </article>
  );
}

function ChatGeneratedImageActions({
  savePending,
  onSave,
  onSaveAsReference,
  onUseAsVisualUniverseSeed,
}: {
  savePending: boolean;
  onSave: () => void;
  onSaveAsReference: () => void;
  onUseAsVisualUniverseSeed?: () => void;
}) {
  const intl = useIntl();

  return (
    <HoverActionCluster>
      <HoverIconActionButton
        disabled={savePending}
        icon={savePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus />}
        label={intl.formatMessage({
          id: "pulse.mediaGallery.saveToGallery",
          defaultMessage: "Save to gallery",
        })}
        onClick={onSave}
      />
      <HoverIconActionButton
        disabled={savePending}
        icon={<Sparkles />}
        label={intl.formatMessage({
          id: "pulse.mediaGallery.saveAsReference",
          defaultMessage: "Save as brand reference",
        })}
        onClick={onSaveAsReference}
      />
      {onUseAsVisualUniverseSeed ? (
        <HoverIconActionButton
          disabled={savePending}
          icon={<Star />}
          label={intl.formatMessage({
            id: "pulse.mediaGallery.useAsVisualUniverseSeed",
            defaultMessage: "Use as visual universe seed",
          })}
          onClick={onUseAsVisualUniverseSeed}
        />
      ) : null}
    </HoverActionCluster>
  );
}

export function ChatGeneratedAssetSkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl bg-card/70 shadow-sm" aria-hidden="true">
      <div className="relative aspect-[9/16] overflow-hidden bg-muted/55">
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-muted/90 via-background/70 to-muted/60" />
        <div className="absolute inset-x-0 top-0 p-2">
          <div className="h-5 w-20 animate-pulse rounded-full bg-background/88 backdrop-blur" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/86 text-primary shadow-sm backdrop-blur">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          </span>
        </div>
      </div>
    </div>
  );
}

function ChatGeneratedAssetActions({
  reviewable,
  canMakePrimary,
  approvePending,
  rejectPending,
  makePrimaryPending,
  canChangeCopy,
  approvalStatus,
  approveLabel,
  onChangeCopy,
  onMakePrimary,
  onApprove,
  onReject,
}: {
  reviewable: boolean;
  canMakePrimary: boolean;
  approvePending: boolean;
  rejectPending: boolean;
  makePrimaryPending: boolean;
  canChangeCopy: boolean;
  approvalStatus: CampaignSlotCardData["approvalStatus"] | null;
  approveLabel: string;
  onChangeCopy: () => void;
  onMakePrimary: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const intl = useIntl();
  const hasPrimaryAction = canMakePrimary;
  const hasCopyAction = canChangeCopy;
  const hasReviewActions = reviewable;
  const busy = approvePending || rejectPending || makePrimaryPending;

  if (!hasCopyAction && !hasPrimaryAction && !hasReviewActions) {
    return null;
  }

  return (
    <HoverActionCluster>
      {hasCopyAction ? (
        <HoverIconActionButton
          disabled={busy}
          icon={<PenLine />}
          label={intl.formatMessage({
            id: "pulse.mediaGallery.changeCopy",
            defaultMessage: "Change copy",
          })}
          onClick={onChangeCopy}
        />
      ) : null}
      {hasPrimaryAction ? (
        <HoverIconActionButton
          disabled={makePrimaryPending || busy}
          icon={makePrimaryPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star />}
          label={intl.formatMessage({
            id: "pulse.mediaGallery.makePrimary",
            defaultMessage: "Make primary",
          })}
          onClick={onMakePrimary}
        />
      ) : null}
      {hasReviewActions ? (
        <>
          <HoverIconActionButton
            disabled={approvePending || rejectPending || approvalStatus === "approved"}
            icon={approvePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check />}
            label={approveLabel}
            onClick={onApprove}
          />
          <HoverIconActionButton
            destructive
            disabled={approvePending || rejectPending || approvalStatus === "rejected"}
            icon={rejectPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X />}
            label={intl.formatMessage({ id: "common.reject", defaultMessage: "Reject" })}
            onClick={onReject}
          />
        </>
      ) : null}
    </HoverActionCluster>
  );
}

function HoverActionCluster({ children }: { children: ReactNode }) {
  return (
    <div className="absolute right-2 top-2 z-20 flex max-w-[calc(100%-1rem)] flex-wrap justify-end gap-1.5 opacity-100 transition sm:translate-y-1 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100 sm:group-focus-within:translate-y-0 sm:group-focus-within:opacity-100">
      {children}
    </div>
  );
}

function HoverIconActionButton({
  destructive = false,
  disabled = false,
  icon,
  label,
  onClick,
}: {
  destructive?: boolean;
  disabled?: boolean;
  icon: ReactElement<{ className?: string }>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/82 text-foreground/68 shadow-sm backdrop-blur transition hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45",
        destructive && "text-destructive/80 hover:bg-destructive/10 hover:text-destructive",
      )}
      disabled={disabled}
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {cloneElement(icon, {
        className: cn("h-3.5 w-3.5", icon.props.className),
      })}
    </button>
  );
}

function canSetAssetAsPrimary(asset: ChatGeneratedAsset) {
  const draft = asset.post?.draft;
  if (!draft) {
    return Boolean(asset.post?.output);
  }

  if (asset.slideId || asset.slideIndex) {
    return draft.carouselSlides.some(
      (slide) => slide.slideId === asset.slideId || slide.slideIndex === asset.slideIndex,
    );
  }

  return true;
}
